// ABOUTME: The modal holding the slot picker for one participant's coaching registration
// ABOUTME: Text comes from platform3 translations; dates follow the participant's language
import { Modal, ModalBody, ModalHeader } from '@execonline-inc/execonline-ui';
import { BookingResult, SlotPicker, SlotPickerView } from '@execonline-inc/slot-picker.private';
import { Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { useTranslationsContext } from '../../Translations/UseTranslationsContext';
import { request } from './request';
import { SchedulerPurpose, schedulerHeading, slotPickerText } from './text';

interface Props {
  schedulerHref: string;
  purpose: SchedulerPurpose;
  // Named in the booking outcome; nothing when the page does not have it.
  participantEmail: Maybe<string>;
  locale: string;
  onBooked: (booked: BookingResult[]) => void;
  onClose: () => void;
}

const CoachingSchedulerModal = ({
  schedulerHref,
  purpose,
  participantEmail,
  locale,
  onBooked,
  onClose,
}: Props) => {
  const { ts } = useTranslationsContext();
  const text = slotPickerText(ts);
  // The booking outcome is a short confirmation: a narrow modal, with its own heading. The modal
  // heading stays for screen readers, which name the dialog by it.
  const [view, setView] = useState<SlotPickerView>('picking');
  const outcome = view === 'outcome';
  // While a booking is in flight the modal cannot be closed and hides its close button: the
  // server books it either way, and closing would hide the result from the participant.
  const booking = view === 'booking';
  const dismiss = () => {
    if (!booking) {
      onClose();
    }
  };

  return (
    <Modal
      open={true}
      size={outcome ? 'xl' : '5xl'}
      onClose={dismiss}
      escapeKey={!booking}
      classNames={{ closeButton: booking ? 'hidden' : '' }}
    >
      {/* Explicit weight and tracking: platform3's global h2 styles are light and spaced. */}
      <ModalHeader className={outcome ? 'sr-only' : 'items-center pb-0 sm:pb-0 sm:pt-8'}>
        <h2 className="text-h6 font-semibold normal-case tracking-normal text-gray-900">
          {schedulerHeading(ts, purpose)}
        </h2>
      </ModalHeader>
      <ModalBody className={outcome ? 'py-10 sm:py-10 sm:pt-12' : 'pb-8 pt-5 sm:pb-8 sm:pt-5'}>
        <SlotPicker
          schedulerHref={schedulerHref}
          sessionWith={nothing()}
          request={request}
          text={text}
          participantEmail={participantEmail}
          locale={locale}
          onBooked={onBooked}
          onViewChange={setView}
          onClose={onClose}
        />
      </ModalBody>
    </Modal>
  );
};

export default observer(CoachingSchedulerModal);
